<template>
  <div class="w-full h-full">
    <!-- Banner：标题淡入 -->
    <div class="relative w-full h-[200px] sm:h-[300px] md:h-[400px]">
      <img
        src="../../assets/img/news_bg.webp"
        alt="service-banner"
        class="w-full object-cover h-full"
      />
      <div class="absolute bottom-0 left-0 right-0 text-white text-[32px] sm:text-[48px] md:text-[60px] lg:text-[72px] font-bold container mx-auto flex flex-col items-start justify-center px-4 sm:px-6 scroll-fade-in">
        <div>资讯中心</div>
        <div class="text-[20px] sm:text-[28px] md:text-[36px] lg:text-[48px] -mt-[8px] sm:-mt-[12px] md:-mt-[16px] lg:-mt-[20px] mb-4 sm:mb-6 md:mb-8 lg:mb-10">
          Information Center
        </div>
      </div>
    </div>

    <!-- 列表区：卡片交错飞入 -->
    <div class="container mx-auto pb-8 pt-4 sm:pt-6 md:pt-8 lg:pt-[68px] px-4 sm:px-6">
      <!-- 加载中状态 -->
      <div 
        v-if="loading"
        class="text-center py-12"
      >
        <div class="text-[#333333] text-lg">加载中...</div>
      </div>

      <!-- 新闻列表 -->
      <template v-else-if="newsList.length > 0">
        <div
          v-for="(item, index) in newsList"
          :key="item.newsId"
          class="bg-[#E5EDFE] rounded-[16px] overflow-hidden mb-6 shadow-sm p-4 sm:p-6 md:p-[34px] cursor-pointer hover:shadow-lg transition-shadow"
          :class="index % 2 === 0 ? 'scroll-fade-in-left' : 'scroll-fade-in-right'"
          :style="{ transitionDelay: (index * 60) + 'ms' }"
          @click="goToDetail(item.newsId)"
      >
          <div class="flex flex-col md:flex-row gap-x-6 lg:gap-x-[62px] sm:gap-6">
          <!-- 左侧：图片 -->
            <div class="shrink-0 w-full md:w-auto">
            <img 
                :src="item.image || '../../assets/img/news_img.webp'"
                :alt="item.title"
                class="w-full md:w-[550px] h-auto md:h-[309px] object-cover rounded-[8px] md:rounded-[8px]"
                @error="handleImageError"
            />
          </div>
          
          <!-- 右侧：文本内容 -->
            <div class="flex-1 flex flex-col justify-between p-0 ">
            <div>
              <!-- 标题 -->
                <h2 class="text-[#333333] text-[18px] sm:text-[20px] md:text-[24px] font-black mb-2 sm:mb-3 leading-tight">
                  {{ item.title }}
              </h2>
              
              <!-- 时间戳 -->
                <div class="text-[#333333] text-[14px] sm:text-[16px] lg:text-[14px] mb-3 sm:mb-4">
                  {{ item.dateTime }}
              </div>
              
              <!-- 简介标题 -->
                <div class="text-[#333333] text-[14px] sm:text-[16px] font-black mb-2">
                简介：
              </div>
              
              <!-- 简介文本 -->
                <p class="text-[#333333] text-[14px] sm:text-[16px] leading-relaxed mb-3 sm:mb-4">
                  {{ item.desc }}
              </p>
            </div>
            
            <!-- 来源信息（右下角） -->
            <div class="flex justify-end mt-auto">
                <div class="text-[#333333] text-[12px] sm:text-[14px] md:text-[16px] font-black">
                  {{ item.newsType || '综合多方行业资讯及企业官方发布信息' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 空状态 -->
      <div
        v-else
        class="text-center py-12"
      >
        <div class="text-[#333333] text-lg">暂无新闻数据</div>
      </div>
      
      <!-- 分页组件 -->
      <div class="flex justify-center md:justify-end mt-6 sm:mt-8 mb-6 sm:mb-8 overflow-x-auto scroll-fade-in">
        <div class="flex items-center gap-1 sm:gap-2">
          <!-- 左箭头 -->
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="pagination-btn pagination-arrow"
            :class="{ 'disabled': currentPage === 1 }"
          >
            &lt;
          </button>
          
          <!-- 页码按钮 -->
          <template
            v-for="page in visiblePages"
            :key="page"
          >
            <button
              v-if="page !== 'ellipsis'"
              @click="goToPage(page)"
              class="pagination-btn pagination-number"
              :class="{ 'active': page === currentPage }"
            >
              {{ page }}
            </button>
            <span
              v-else
              class="pagination-ellipsis"
            >...</span>
          </template>
          
          <!-- 右箭头 -->
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="pagination-btn pagination-arrow"
            :class="{ 'disabled': currentPage === totalPages }"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { useRouter } from "vue-router";
import { newsApi, type NewsItem } from "@/api";
import { RequestError } from "@/utils/request";

const router = useRouter();

// 新闻列表数据
const newsList = ref<NewsItem[]>([]);
const loading = ref(false);

// 分页相关
const currentPage = ref(1);
const pageSize = ref(10); // 每页显示数量
const totalCount = ref(0); // 总记录数

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(totalCount.value / pageSize.value);
});

// 加载新闻列表
const loadNewsList = async () => {
  loading.value = true;
  try {
    const response = await newsApi.getNewsList({
      pageIndex: currentPage.value,
      pageSize: pageSize.value,
    });

    if (response.code === 200 && response.data) {
      newsList.value = response.data.items || [];
      totalCount.value = response.data.totalCount || 0;

      // 数据加载完成后，重新观察动画元素
      await nextTick();
      observeElements();
    } else {
      console.warn("新闻列表响应格式异常:", response);
    }
  } catch (error) {
    console.error("加载新闻列表失败:", error);
    if (error instanceof RequestError) {
      console.error("错误信息:", error.message);
    }
  } finally {
    loading.value = false;
  }
};

// 图片加载错误处理
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.src = "../../assets/img/news_img.webp";
};

// 跳转到新闻详情页
const goToDetail = (newsId: string) => {
  router.push(`/news/${newsId}.html`);
};

// 计算可见的页码
const visiblePages = computed(() => {
  const pages: (number | "ellipsis")[] = [];
  const total = totalPages.value;
  
  if (total <= 7) {
    // 如果总页数少于等于7，显示所有页码
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // 总页数大于7的情况
    if (currentPage.value <= 3) {
      // 当前页在前3页
      for (let i = 1; i <= 3; i++) {
        pages.push(i);
      }
      pages.push("ellipsis");
      pages.push(total);
    } else if (currentPage.value >= total - 2) {
      // 当前页在后3页
      pages.push(1);
      pages.push("ellipsis");
      for (let i = total - 2; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // 当前页在中间
      pages.push(1);
      pages.push("ellipsis");
      pages.push(currentPage.value - 1);
      pages.push(currentPage.value);
      pages.push(currentPage.value + 1);
      pages.push("ellipsis");
      pages.push(total);
    }
  }
  
  return pages;
});

// 跳转到指定页
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    currentPage.value = page;
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: "smooth" });
    // 加载对应页的数据
    loadNewsList();
  }
};

// ===== 滚动动画：列表入场效果 =====

const animatedElements = new Set<HTMLElement>();

const observerOptions: IntersectionObserverInit = {
  root: null,
  rootMargin: "0px",
  threshold: 0.1,
};

let observer: IntersectionObserver | null = null;

// 观察动画元素的函数
const observeElements = () => {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          !animatedElements.has(entry.target as HTMLElement)
        ) {
          const el = entry.target as HTMLElement;
          el.classList.add("fade-in-visible");
          animatedElements.add(el);
        }
      });
    }, observerOptions);
  }

  const elements = document.querySelectorAll(
    ".scroll-fade-in, .scroll-fade-in-left, .scroll-fade-in-right"
  );
  elements.forEach((el) => {
    if (observer && !animatedElements.has(el as HTMLElement)) {
      observer.observe(el);
  }
  });
};

// 监听页码变化
watch(currentPage, () => {
  loadNewsList();
});

onMounted(() => {
  // 初始加载数据
  loadNewsList();

  // 初始观察元素
  observeElements();
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style scoped>
:deep(.el-select) {
  width: 100%;
}

:deep(.el-input__wrapper) {
  background-color: #e8f0f5;
  border-radius: 8px;
  box-shadow: none !important;
  padding: 12px 16px;
  border: none;
}

:deep(.el-input__inner) {
  color: #333;
  font-weight: 600;
  font-size: 16px;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: none !important;
}

:deep(.el-input.is-focus .el-input__wrapper) {
  box-shadow: 0 0 0 1px #409eff inset !important;
}

:deep(.el-select .el-input .el-select__caret) {
  color: #333;
  font-size: 14px;
}

:deep(.el-input.is-disabled .el-input__wrapper) {
  background-color: #f5f7fa;
  cursor: not-allowed;
}

:deep(.el-input.is-disabled .el-input__inner) {
  color: #c0c4cc;
}

/* 分页组件样式 */
.pagination-btn {
  min-width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s;
}

@media (min-width: 640px) {
  .pagination-btn {
    min-width: 36px;
    height: 36px;
    font-size: 15px;
  }
}

@media (min-width: 768px) {
  .pagination-btn {
    min-width: 40px;
    height: 40px;
    font-size: 16px;
  }
}

.pagination-arrow {
  background-color: #e5e5e5;
  color: #999999;
}

.pagination-arrow:hover:not(.disabled) {
  background-color: #d5d5d5;
}

.pagination-arrow.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-number {
  background-color: #e5e5e5;
  color: #666666;
}

.pagination-number:hover:not(.active) {
  background-color: #d5d5d5;
}

.pagination-number.active {
  background-color: #1d3c7d;
  color: white;
}

.pagination-ellipsis {
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
  font-size: 14px;
}

@media (min-width: 640px) {
  .pagination-ellipsis {
    min-width: 36px;
    height: 36px;
    font-size: 15px;
  }
}

@media (min-width: 768px) {
  .pagination-ellipsis {
    min-width: 40px;
    height: 40px;
  font-size: 16px;
  }
}
</style>
